// Employee availability (SCR-079, DLG-059; legacy api `modules/Scheduling` AvailabilityController and
// UpdateAvailabilityAction; field note backoffice.employees.availability): when each employee can work on each weekday,
// as location config (`labor.employee_availability`, id = the employee's id, ADR-015). The Back Office's Availability
// tab sets it a day at a time; the schedule reads it for the week grid's markers, the shift dialog's line and its
// "Availability Conflict" confirm, and the conflict detector (core `labor/availability`).
//
// Legacy keeps each saved week as dated rows (`effective_from` / `effective_until`) but reads only the current ones; Next
// keeps the current week and the day it took effect. An employee with no record yet is available all day, every day:
// legacy seeds that week for every new employee (`EmployeeObserver::created`). The import writes each imported
// employee's legacy days, an empty week when legacy had none (every day "Not set").
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { EmployeeId, LocalDate, LocationId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten } from '../config/index.ts';

const WallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const AvailabilityDay = z
  .object({
    /** 0 = Monday .. 6 = Sunday (legacy `day_of_week`), whatever the location's week start. */
    dayOfWeek: z.int().min(0).max(6),
    available: z.boolean(),
    /** "HH:MM"; null on an unavailable day, and on an available day means all day. */
    startTime: WallTime.nullable(),
    endTime: WallTime.nullable(),
  })
  .meta({ id: 'AvailabilityDay' });

const Week = z.array(AvailabilityDay).max(7).refine((days) => new Set(days.map((d) => d.dayOfWeek)).size === days.length, { message: 'one entry per weekday' });

export const EmployeeAvailabilityView = z
  .object({
    employeeId: EmployeeId,
    /** The days with a record; a weekday left out is "Not set". */
    days: z.array(AvailabilityDay),
    /** The day the current week took effect; null when the employee has none recorded (available all day, every day). */
    effectiveFrom: LocalDate.nullable(),
  })
  .meta({ id: 'EmployeeAvailabilityView' });

/** Availability › a day › Save (legacy `PUT /v1/employee-availabilities/{employee}`): replaces the employee's week. */
export const SetEmployeeAvailability = defineCommand({
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'labor.timecards.manage', risk: 'low', dryRun: false, agent: { expose: false },
  name: 'labor.setEmployeeAvailability',
  summary: "Set an employee's weekly availability: each weekday available all day, from a start to an end, or unavailable.",
  input: z.object({ locationId: LocationId, employeeId: EmployeeId, days: Week }),
  output: EmployeeAvailabilityView,
  errors: [CloudAuthoritative],
  legacy: ['PUT /v1/employee-availabilities/{employee_id}'],
});

/** Employees › Availability, and the schedule: every active employee's current week. */
export const EmployeeAvailability = defineQuery({
  name: 'labor.employeeAvailability',
  summary: "Every employee's weekly availability at a location (all day every day when none is recorded).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ employees: z.array(EmployeeAvailabilityView) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/employee-availabilities'],
});

export const availabilityCommands = [SetEmployeeAvailability] as const;
export const availabilityQueries = [EmployeeAvailability] as const;
